// Business page: client payee splits and cashout requests (Premium; client payouts on).
// A split sends a client's share of an invoice payment straight into that
// client's own Stripe account. Cashout requests are approved or held here;
// approving pays the client's own Stripe balance out to their bank.
import React, { useCallback, useEffect, useState } from 'react';
import clientPortalAPI from './clientPortalApi';
import { formatApiError } from '../../utils/errorMessage';

const SPLIT_STATUS = { fee_pending: 'Waiting for Stripe fee', partial: 'Partly sent', failed: 'Not sent' };
const PARTY = { client: 'client', business: 'business' };
const STATUS = { requested: 'Requested', held: 'On hold', approved: 'Approved', paid: 'Paid', failed: 'Failed', canceled: 'Canceled' };

export default function ClientCashouts({ businessId, clients = [] }) {
  const [requests, setRequests] = useState([]);
  const [problems, setProblems] = useState({ splits: [], moves: [], owed: [] });
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ ar_id: '', payee_portal_client_id: '', share_type: 'percent', share_value: '' });
  const load = useCallback(async () => {
    try { setRequests((await clientPortalAPI.cashouts(businessId)).data.requests); } catch (e) { setError(formatApiError(e, 'Could not load cashout requests')); }
    try { setProblems((await clientPortalAPI.splitProblems(businessId)).data || { splits: [], moves: [] }); } catch (e) { /* shown with the next action */ }
  }, [businessId]);
  useEffect(() => { load(); }, [load]);
  const act = async (id, action) => {
    setBusy(true); setError('');
    try { await clientPortalAPI.cashoutAction(businessId, id, action); await load(); } catch (e) { setError(formatApiError(e, 'Could not update the request')); }
    setBusy(false);
  };
  const retry = async (fn, id) => {
    setBusy(true); setError('');
    try { await fn(businessId, id); await load(); } catch (e) { setError(formatApiError(e, 'Could not retry')); }
    setBusy(false);
  };
  const saveSplit = async (e) => {
    e.preventDefault();
    setBusy(true); setError(''); setMsg('');
    try {
      await clientPortalAPI.setClientSplit(businessId, form.ar_id, { payee_portal_client_id: Number(form.payee_portal_client_id),
        share_type: form.share_type, share_value: form.share_value });
      setMsg('Client share saved. When this invoice is paid by card, the share goes to the client\'s Stripe account.');
    } catch (err) { setError(formatApiError(err, 'Could not save the client share')); }
    setBusy(false);
  };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  return (
    <div aria-label="Client cashouts" className="border rounded p-2 text-xs space-y-2">
      <p className="font-medium">Client share of an invoice</p>
      <form onSubmit={saveSplit} className="flex flex-wrap gap-2 items-center">
        <input aria-label="Invoice or recurring template id" className="border rounded px-1 w-28" placeholder="Invoice id" value={form.ar_id} onChange={set('ar_id')} />
        <select aria-label="Client who receives the share" className="border rounded px-1" value={form.payee_portal_client_id} onChange={set('payee_portal_client_id')}>
          <option value="">Client…</option>
          {clients.map((c) => <option key={c.id} value={c.id}>{c.display_name || c.email}</option>)}
        </select>
        <select aria-label="Share type" className="border rounded px-1" value={form.share_type} onChange={set('share_type')}>
          <option value="percent">Percent</option><option value="fixed">Fixed amount</option>
        </select>
        <input aria-label="Share" className="border rounded px-1 w-20" inputMode="decimal" value={form.share_value} onChange={set('share_value')} />
        <button type="submit" disabled={busy || !form.ar_id || !form.payee_portal_client_id || !form.share_value} className="border rounded px-2">Save share</button>
      </form>
      <p className="font-medium">Cashout requests</p>
      {requests.length === 0 ? <p>No cashout requests.</p> : (
        <ul>
          {requests.map((r) => (
            <li key={r.id} className="flex gap-2 items-center">
              <span>{r.display_name || r.email}: ${r.amount} ({STATUS[r.status] || r.status})</span>
              {['requested', 'held'].includes(r.status) && (
                <>
                  <button type="button" disabled={busy} className="border rounded px-2" onClick={() => act(r.id, 'approve')}>Approve</button>
                  {r.status === 'requested' && <button type="button" disabled={busy} className="border rounded px-2" onClick={() => act(r.id, 'hold')}>Hold</button>}
                  <button type="button" disabled={busy} className="border rounded px-2" onClick={() => act(r.id, 'cancel')}>Cancel</button>
                </>
              )}
              {r.status === 'approved' && !r.payout_confirmed && r.failure_message && (
                <><span>{r.failure_message}</span><button type="button" disabled={busy} className="border rounded px-2" onClick={() => act(r.id, 'retry')}>Retry</button></>
              )}
            </li>
          ))}
        </ul>
      )}
      {(problems.splits.length > 0 || problems.moves.length > 0 || (problems.payout_setup || []).length > 0) && (
        <div aria-label="Payment shares that need attention">
          <p className="font-medium">Payment shares that need attention</p>
          <p>A share held for a client's payout schedule is sent after a successful payout schedule Retry, or with the share's own Retry, which sets and checks the schedule again first. Neither sends anything twice.</p>
          <ul>
            {problems.splits.map((t) => (
              <li key={`s${t.id}`} className="flex gap-2 items-center">
                <span>Invoice payment {t.portal_payment_id} ({t.client}): {SPLIT_STATUS[t.status] || t.status}. Client share {t.client_sent ? 'sent' : 'not sent'}, business part {t.business_sent ? 'sent' : 'not sent'}.{t.failure_message ? ` ${t.failure_message}` : ''}</span>
                <button type="button" disabled={busy} className="border rounded px-2" onClick={() => retry(clientPortalAPI.retrySplit, t.id)}>Retry</button>
              </li>
            ))}
            {(problems.payout_setup || []).map((a) => (
              <li key={`a${a.id}`} className="flex gap-2 items-center">
                <span>{a.client}: manual payouts are not confirmed, so money already in this client's Stripe balance may pay out on Stripe's default schedule. New shares for this client are held until it is confirmed. {a.failure_message}</span>
                <button type="button" disabled={busy} className="border rounded px-2" onClick={() => retry(clientPortalAPI.retryManualPayouts, a.id)}>Retry</button>
              </li>
            ))}
            {problems.moves.map((m) => (
              <li key={`m${m.id}`} className="flex gap-2 items-center">
                <span>{m.kind === 'repay' ? 'Pay back' : 'Take back'} ${m.amount} for the {PARTY[m.party]} share ({m.source}): {m.status === 'failed' ? 'failed' : 'waiting'}.{m.failure_message ? ` ${m.failure_message}` : ''}</span>
                <button type="button" disabled={busy} className="border rounded px-2" onClick={() => retry(clientPortalAPI.retrySplitMove, m.id)}>Retry</button>
              </li>
            ))}
          </ul>
        </div>
      )}
      {(problems.owed || []).length > 0 && (
        <div aria-label="Amounts owed on split invoices">
          <p className="font-medium">Amounts your business owes on split invoices: ${problems.owed_total}</p>
          <p>Stripe fees kept on refunds, dispute fees, and shares that could not be taken back. Shown for your records; nothing is charged automatically.</p>
          <ul>{problems.owed.map((o) => <li key={`o${o.id}`}>${o.amount}: {o.memo}</li>)}</ul>
        </div>
      )}
      {msg && <p>{msg}</p>}
      {error && <p role="alert" className="text-red-600">{error}</p>}
    </div>
  );
}
